import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { AuthContext } from '../context/AuthContext';
import { db } from '../config/firebaseConfig';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';

const PAGE_SIZE = 10;

// pequeno debounce local (mesmo padrão usado em outras telas)
function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function Leads() {
  const intl = useIntl();
  const { user } = useContext(AuthContext);

  // tabela
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // busca e filtros
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [statusFilter, setStatusFilter] = useState(''); // '', 'new', 'contacted', 'qualified', 'unqualified', 'closed'
  const [sourceFilter, setSourceFilter] = useState(''); // '', 'whatsapp', 'website', etc.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState([null]);
  const [isLastPage, setIsLastPage] = useState(false);

  // contadores (cards-resumo)
  const [totalCount, setTotalCount] = useState(0);
  const [countNew, setCountNew] = useState(0);
  const [countContacted, setCountContacted] = useState(0);
  const [countQualified, setCountQualified] = useState(0);
  const [countUnqualified, setCountUnqualified] = useState(0);

  // versão de dados para recarregar listagem (ex.: ao atualizar status/deletar)
  const [dataVersion, setDataVersion] = useState(0);

  // fontes dinâmicas (descobertas a partir da listagem atual)
  const sourcesFromData = useMemo(() => {
    const s = new Set();
    leads.forEach((l) => l?.source && s.add(String(l.source)));
    return Array.from(s).sort();
  }, [leads]);

  // reset de paginação ao mudar filtros/busca
  useEffect(() => {
    setCurrentPage(1);
    setPageCursors([null]);
    setIsLastPage(false);
  }, [debouncedSearch, statusFilter, sourceFilter, dateFrom, dateTo]);

  // contadores gerais (sem filtros)
  useEffect(() => {
    if (!user?.uid) return;

    const col = collection(db, `users/${user.uid}/leads`);

    const run = async () => {
      try {
        const totalSnap = await getCountFromServer(col);
        setTotalCount(totalSnap.data().count);

        const statuses = [
          ['new', setCountNew],
          ['contacted', setCountContacted],
          ['qualified', setCountQualified],
          ['unqualified', setCountUnqualified],
        ];

        await Promise.all(
          statuses.map(async ([st, setter]) => {
            const qSt = query(col, where('status', '==', st));
            const snap = await getCountFromServer(qSt);
            setter(snap.data().count);
          })
        );
      } catch (err) {
        // silencioso para não poluir UI; logs ajudam debug
        console.error('Error fetching lead counters:', err);
      }
    };

    run();
  }, [user, dataVersion]);

  // consulta leads
  const fetchLeads = useCallback(async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const col = collection(db, `users/${user.uid}/leads`);
      let q;

      // busca rápida por termos (array-contains)
      if (debouncedSearch) {
        const s = debouncedSearch.toLowerCase();
        q = query(col, where('searchable_terms', 'array-contains', s), orderBy('createdAt', 'desc'), limit(PAGE_SIZE));
      } else {
        // filtros combinados + paginação
        const constraints = [];

        // igualdade (podem somar várias)
        if (statusFilter) constraints.push(where('status', '==', statusFilter));
        if (sourceFilter) constraints.push(where('source', '==', sourceFilter));

        // range em createdAt e orderBy no mesmo campo
        const hasDateFrom = !!dateFrom;
        const hasDateTo = !!dateTo;

        if (hasDateFrom) {
          constraints.push(where('createdAt', '>=', new Date(`${dateFrom}T00:00:00`)));
        }
        if (hasDateTo) {
          constraints.push(where('createdAt', '<=', new Date(`${dateTo}T23:59:59`)));
        }

        constraints.push(orderBy('createdAt', 'desc'));
        const cursor = pageCursors[currentPage - 1];
        if (cursor) constraints.push(startAfter(cursor));
        constraints.push(limit(PAGE_SIZE));

        q = query(col, ...constraints);
      }

      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLeads(docs);

      // paginação
      if (!debouncedSearch) {
        setIsLastPage(snap.docs.length < PAGE_SIZE);
        const last = snap.docs[snap.docs.length - 1];
        if (last && pageCursors.length === currentPage) {
          setPageCursors((prev) => [...prev, last]);
        }
      } else {
        setIsLastPage(true); // consulta por busca não pagina (simplificação)
      }
    } catch (err) {
      console.error('Erro ao buscar leads:', err);
      setError(
        intl.formatMessage({
          id: 'leads.errorLoading',
          defaultMessage: 'Erro ao carregar leads. Verifique o console para mais detalhes.',
        })
      );
    } finally {
      setLoading(false);
    }
  }, [user, debouncedSearch, statusFilter, sourceFilter, dateFrom, dateTo, currentPage, pageCursors, intl]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads, dataVersion]);

  const handleNextPage = () => {
    if (!isLastPage && !loading) setCurrentPage((p) => p + 1);
  };
  const handlePrevPage = () => {
    if (currentPage > 1 && !loading) setCurrentPage((p) => p - 1);
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    if (!user?.uid || !leadId || !newStatus) return;
    try {
      await updateDoc(doc(db, `users/${user.uid}/leads`, leadId), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      toast.success(
        intl.formatMessage(
          { id: 'leads.statusUpdated', defaultMessage: 'Status atualizado para {status}.' },
          { status: newStatus }
        )
      );
      setDataVersion((v) => v + 1);
    } catch (err) {
      console.error('Erro ao atualizar status do lead:', err);
      toast.error(intl.formatMessage({ id: 'leads.statusUpdateError', defaultMessage: 'Erro ao atualizar status.' }));
    }
  };

  const removeLead = async (leadId) => {
    if (!user?.uid || !leadId) return;
    if (!confirm(intl.formatMessage({ id: 'leads.confirmDelete', defaultMessage: 'Deseja excluir este lead?' }))) return;
    try {
      await deleteDoc(doc(db, `users/${user.uid}/leads`, leadId));
      toast.success(intl.formatMessage({ id: 'leads.deleted', defaultMessage: 'Lead excluído.' }));
      setDataVersion((v) => v + 1);
    } catch (err) {
      console.error('Erro ao excluir lead:', err);
      toast.error(intl.formatMessage({ id: 'leads.deleteError', defaultMessage: 'Erro ao excluir lead.' }));
    }
  };

  const statusOptions = useMemo(
    () => [
      { value: '', label: intl.formatMessage({ id: 'leads.filters.status.all', defaultMessage: 'Todos status' }) },
      { value: 'new', label: intl.formatMessage({ id: 'leads.status.new', defaultMessage: 'Novo' }) },
      { value: 'contacted', label: intl.formatMessage({ id: 'leads.status.contacted', defaultMessage: 'Contactado' }) },
      { value: 'qualified', label: intl.formatMessage({ id: 'leads.status.qualified', defaultMessage: 'Qualificado' }) },
      { value: 'unqualified', label: intl.formatMessage({ id: 'leads.status.unqualified', defaultMessage: 'Desqualificado' }) },
      { value: 'closed', label: intl.formatMessage({ id: 'leads.status.closed', defaultMessage: 'Fechado' }) },
    ],
    [intl]
  );

  return (
    <div className="la">
      <div className="dashboard-content">
        <h1 className="page-title">
          {intl.formatMessage({ id: 'leads.title', defaultMessage: 'Leads' })}
        </h1>
        <p className="subtitle-muted">
          {intl.formatMessage({
            id: 'leads.subtitle',
            defaultMessage: 'Acompanhe, filtre e atualize seus leads.',
          })}
        </p>

        {/* Cards resumo */}
        <section className="card mb-16">
          <div className="card-body">
            <div className="grid-4">
              <div className="stat">
                <div className="stat-title">{intl.formatMessage({ id: 'leads.card.total', defaultMessage: 'Total' })}</div>
                <div className="stat-value">{totalCount}</div>
              </div>
              <div className="stat">
                <div className="stat-title">{intl.formatMessage({ id: 'leads.status.new', defaultMessage: 'Novo' })}</div>
                <div className="stat-value">{countNew}</div>
              </div>
              <div className="stat">
                <div className="stat-title">{intl.formatMessage({ id: 'leads.status.contacted', defaultMessage: 'Contactado' })}</div>
                <div className="stat-value">{countContacted}</div>
              </div>
              <div className="stat">
                <div className="stat-title">{intl.formatMessage({ id: 'leads.status.qualified', defaultMessage: 'Qualificado' })}</div>
                <div className="stat-value">{countQualified}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Filtros e busca */}
        <section className="card mb-16">
          <div className="card-header">
            <div className="section-title">
              {intl.formatMessage({ id: 'leads.filters.title', defaultMessage: 'Filtros' })}
            </div>
          </div>
          <div className="card-body grid-4">
            <div className="form-group">
              <label>{intl.formatMessage({ id: 'leads.filters.status', defaultMessage: 'Status' })}</label>
              <select
                className="input-field"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label={intl.formatMessage({ id: 'leads.filters.status', defaultMessage: 'Status' })}
              >
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{intl.formatMessage({ id: 'leads.filters.source', defaultMessage: 'Origem' })}</label>
              <select
                className="input-field"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                aria-label={intl.formatMessage({ id: 'leads.filters.source', defaultMessage: 'Origem' })}
              >
                <option value="">{intl.formatMessage({ id: 'leads.filters.source.all', defaultMessage: 'Todas origens' })}</option>
                {sourcesFromData.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{intl.formatMessage({ id: 'leads.filters.dateFrom', defaultMessage: 'De' })}</label>
              <input
                className="input-field"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>{intl.formatMessage({ id: 'leads.filters.dateTo', defaultMessage: 'Até' })}</label>
              <input
                className="input-field"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>{intl.formatMessage({ id: 'leads.search', defaultMessage: 'Busca' })}</label>
              <input
                className="input-field"
                placeholder={intl.formatMessage({
                  id: 'leads.searchPlaceholder',
                  defaultMessage: 'Buscar por nome, email, telefone…',
                })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Tabela */}
        <section className="card">
          <div className="card-header">
            <div className="section-title">
              {intl.formatMessage({ id: 'leads.table.title', defaultMessage: 'Lista de Leads' })}
            </div>
          </div>

          <div className="card-body">
            {loading ? (
              <div className="loading-inline">
                {intl.formatMessage({ id: 'loading.label', defaultMessage: 'Carregando…' })}
              </div>
            ) : error ? (
              <div className="status-box danger">{error}</div>
            ) : leads.length === 0 ? (
              <div className="status-box">
                {intl.formatMessage({ id: 'leads.empty', defaultMessage: 'Nenhum lead encontrado.' })}
              </div>
            ) : (
              <div className="table-wrap">
                <table className="la-table">
                  <thead>
                    <tr>
                      <th>{intl.formatMessage({ id: 'leads.table.name', defaultMessage: 'Nome' })}</th>
                      <th>{intl.formatMessage({ id: 'leads.table.email', defaultMessage: 'Email' })}</th>
                      <th>{intl.formatMessage({ id: 'leads.table.phone', defaultMessage: 'Telefone' })}</th>
                      <th>{intl.formatMessage({ id: 'leads.table.source', defaultMessage: 'Origem' })}</th>
                      <th>{intl.formatMessage({ id: 'leads.table.status', defaultMessage: 'Status' })}</th>
                      <th>{intl.formatMessage({ id: 'leads.table.createdAt', defaultMessage: 'Criado em' })}</th>
                      <th>{intl.formatMessage({ id: 'user.actions', defaultMessage: 'Ações' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => {
                      const createdAt =
                        l?.createdAt?.toDate?.() instanceof Date
                          ? l.createdAt.toDate()
                          : l?.createdAt instanceof Date
                          ? l.createdAt
                          : null;
                      return (
                        <tr key={l.id}>
                          <td>{l.name || '—'}</td>
                          <td className="break-anywhere">{l.email || '—'}</td>
                          <td className="break-anywhere">{l.phone || '—'}</td>
                          <td>{l.source || '—'}</td>
                          <td>
                            <span className={`chip chip-${l.status || 'unknown'}`}>
                              {intl.formatMessage({
                                id: `leads.status.${l.status || 'unknown'}`,
                                defaultMessage: l.status || '—',
                              })}
                            </span>
                          </td>
                          <td>{createdAt ? createdAt.toLocaleString() : '—'}</td>
                          <td>
                            <div className="row gap-8">
                              <button
                                type="button"
                                className="secondary-button"
                                onClick={() => updateLeadStatus(l.id, 'contacted')}
                                title={intl.formatMessage({ id: 'leads.actions.markContacted', defaultMessage: 'Marcar como contactado' })}
                              >
                                {intl.formatMessage({ id: 'leads.actions.markContacted', defaultMessage: 'Contactado' })}
                              </button>
                              <button
                                type="button"
                                className="secondary-button"
                                onClick={() => updateLeadStatus(l.id, 'qualified')}
                                title={intl.formatMessage({ id: 'leads.actions.markQualified', defaultMessage: 'Marcar como qualificado' })}
                              >
                                {intl.formatMessage({ id: 'leads.actions.markQualified', defaultMessage: 'Qualificado' })}
                              </button>
                              <button
                                type="button"
                                className="btn-danger"
                                onClick={() => removeLead(l.id)}
                              >
                                {intl.formatMessage({ id: 'general.delete', defaultMessage: 'Excluir' })}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Paginação */}
                {!debouncedSearch && (
                  <div className="card-footer actions">
                    <button className="btn-secondary" onClick={handlePrevPage} disabled={currentPage === 1 || loading}>
                      &larr; {intl.formatMessage({ id: 'pagination.previous', defaultMessage: 'Anterior' })}
                    </button>
                    <span className="muted">
                      {intl.formatMessage({ id: 'pagination.page', defaultMessage: 'Página' })} {currentPage}
                    </span>
                    <button className="btn-secondary" onClick={handleNextPage} disabled={isLastPage || loading}>
                      {intl.formatMessage({ id: 'pagination.next', defaultMessage: 'Próxima' })} &rarr;
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
